import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from 'lib/utils';

const buttonVariants = cva(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded font-medium transition-colors duration-fast ease-standard focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-400 [&_svg]:pointer-events-none [&_svg]:shrink-0',
    {
        variants: {
            variant: {
                default: 'bg-primary text-primary-foreground hover:bg-navy-500',
                secondary:
                    'bg-background text-primary border border-input hover:bg-accent',
                outline:
                    'bg-background text-foreground border border-input hover:bg-muted',
                ghost: 'bg-transparent text-neutral-700 hover:bg-muted',
                destructive:
                    'bg-destructive text-destructive-foreground hover:bg-destructive/90',
                // 표 행 액션 등 반복 노출되는 삭제 버튼 — 행마다 빨간 면이 깔리지 않도록 outline
                'destructive-outline':
                    'bg-background text-destructive border border-input hover:bg-red-50 hover:border-destructive/40',
                warning: 'bg-warning text-warning-foreground hover:bg-warning/90',
                link: 'text-info underline-offset-4 hover:underline',
            },
            size: {
                sm: 'h-8 px-3 text-xs [&_svg]:size-[14px]',
                default: 'h-9 px-4 text-md [&_svg]:size-4',
                lg: 'h-10 px-5 text-md [&_svg]:size-4',
                icon: 'h-9 w-9 [&_svg]:size-4',
                'icon-sm': 'h-8 w-8 [&_svg]:size-[14px]',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    }
);

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : 'button';
        return (
            <Comp
                ref={ref}
                className={cn(buttonVariants({ variant, size, className }))}
                {...props}
            />
        );
    }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
